import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Trash2, UserCircle2 } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

/**
 * Account-level settings (as opposed to per-business Website Settings).
 * Currently hosts the "Delete My Account" Danger Zone — permanent hard
 * delete with a type-to-confirm modal.
 *
 * When the user confirms the deletion:
 *   1. Frontend POSTs `/api/user/account/delete` with `confirm="DELETE"`
 *   2. Backend cancels any active recurring Stripe subscriptions
 *   3. Backend hard-deletes every row keyed by this user across the DB
 *   4. We logout locally + redirect to `/` (auth session is server-invalidated
 *      by the delete, so any subsequent request would 401 anyway).
 */
export default function AccountTab() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const isConfirmed = confirmText === "DELETE";

  const onDelete = async () => {
    if (!isConfirmed) return;
    setDeleting(true);
    try {
      await api.post("/user/account/delete", { confirm: "DELETE" });
      toast.success("Your account has been permanently deleted.");
      // Also clear the local session on the client so we're consistent
      // even if the server-side session-purge hadn't hit this tab yet.
      try { await logout(); } catch { /* best-effort */ }
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete account. Please try again.");
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="account-tab">
      {/* Profile summary */}
      <section className="bg-white border border-emerald-100 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <UserCircle2 className="w-6 h-6 text-emerald-600" strokeWidth={1.75} />
          <h2 className="font-display text-xl font-extrabold text-slate-900">Your Account</h2>
        </div>
        <dl className="grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="text-xs uppercase tracking-widest text-slate-500 mb-1">Email</dt>
            <dd data-testid="account-email" className="text-slate-900 font-medium">{user?.email || "—"}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-widest text-slate-500 mb-1">Plan</dt>
            <dd data-testid="account-plan" className="text-slate-900 font-medium capitalize">{user?.plan_id || "free"}</dd>
          </div>
        </dl>
      </section>

      {/* Danger zone */}
      <section
        className="bg-white border-2 border-red-200 rounded-2xl p-6"
        data-testid="danger-zone"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" strokeWidth={1.75} />
          <div className="flex-1">
            <h2 className="font-display text-xl font-extrabold text-red-900">Danger Zone</h2>
            <p className="text-sm text-slate-700 mt-2 leading-relaxed">
              Permanently delete your account, websites, images, business data, audience plan, 30-day plan,
              products/services, and any connected data. Your subscription will be cancelled and you will
              not be charged again. This action cannot be undone.
            </p>
            <Button
              variant="destructive"
              onClick={() => { setConfirmText(""); setShowModal(true); }}
              data-testid="delete-account-btn"
              className="mt-5 gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete My Account
            </Button>
          </div>
        </div>
      </section>

      {/* Type-to-confirm modal */}
      <Dialog open={showModal} onOpenChange={(v) => !deleting && setShowModal(v)}>
        <DialogContent data-testid="delete-account-modal" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-red-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" strokeWidth={2} />
              Permanently delete your account?
            </DialogTitle>
            <DialogDescription className="text-slate-700 leading-relaxed pt-2">
              Deleting your account is <b>permanent</b>. Your websites, data, images, plans, and billing
              access will be deleted immediately. Your subscription will be cancelled and you will not be
              charged again. If you return later, you will need to create a new account and start over.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="text-sm font-medium text-slate-800 block">
              Type <span className="font-mono px-1.5 py-0.5 bg-slate-100 rounded text-red-700">DELETE</span> to confirm:
            </label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              data-testid="delete-confirm-input"
              disabled={deleting}
              autoFocus
              className="font-mono"
            />
          </div>
          <DialogFooter className="gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowModal(false)}
              disabled={deleting}
              data-testid="delete-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={onDelete}
              disabled={!isConfirmed || deleting}
              data-testid="delete-confirm-btn"
              className="gap-2"
            >
              <Trash2 className="w-4 h-4" />
              {deleting ? "Deleting…" : "Delete Permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
